// Shared format utilities — date/time parsing and display
import { FALLBACK_TZ } from '../../config/env';

export const DISPLAY_TZ = FALLBACK_TZ;

// The backend sends naive UTC timestamps with no timezone designator
// (e.g. "2026-07-02T04:22:32.926000" — no trailing "Z" or "+00:00").
// JS's Date parser treats a date-time string with no offset as LOCAL
// time, not UTC, so `new Date(thatString)` silently skips the
// UTC → viewer-timezone conversion entirely (it just relabels the raw
// UTC wall-clock numbers as if they were already local). Append "Z"
// so the string is parsed as UTC before we ever format/compare it.
export function parseApiDate(value) {
  if (value == null || value instanceof Date) return value;
  if (typeof value === 'string' && !/(Z|[+-]\d{2}:?\d{2})$/.test(value)) {
    return new Date(value + 'Z');
  }
  return new Date(value);
}

const dateFmt = new Intl.DateTimeFormat('en-US', { timeZone: DISPLAY_TZ, month: 'short', day: 'numeric', year: 'numeric' });
const monthDayFmt = new Intl.DateTimeFormat('en-US', { timeZone: DISPLAY_TZ, month: 'short', day: 'numeric' });
const timeFmt = new Intl.DateTimeFormat('en-GB', { timeZone: DISPLAY_TZ, hour: '2-digit', minute: '2-digit', hour12: false });
const weekdayFmt = new Intl.DateTimeFormat('en-US', { timeZone: DISPLAY_TZ, weekday: 'long' });
const dateOnlyJstFmt = new Intl.DateTimeFormat('en-GB', { timeZone: DISPLAY_TZ, year: 'numeric', month: '2-digit', day: '2-digit' });
const dateTimeJstFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: DISPLAY_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
});

export const fmtDate = d => dateFmt.format(parseApiDate(d));
export const fmtDateTime = d => `${monthDayFmt.format(parseApiDate(d))} • ${timeFmt.format(parseApiDate(d))}`;
export const fmtWeekday = d => weekdayFmt.format(parseApiDate(d));

/** DD/MM/YYYY in JST — parses naive backend UTC as UTC first. */
export const formatDateJst = d => {
  const date = parseApiDate(d);
  return date && !Number.isNaN(date.getTime()) ? dateOnlyJstFmt.format(date) : '';
};

/** DD/MM/YYYY HH:mm:ss in JST — parses naive backend UTC as UTC first. */
export const formatDateTimeJst = d => {
  const date = parseApiDate(d);
  return date && !Number.isNaN(date.getTime())
    ? dateTimeJstFmt.format(date).replace(', ', ' ')
    : '';
};

export function timeAgo(d) {
  const ms = Date.now() - parseApiDate(d).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return m + 'm ago';
  const h = Math.floor(m / 60);
  if (h < 24) return h + 'h ago';
  const days = Math.floor(h / 24);
  if (days < 7) return days + 'd ago';
  return fmtDate(d);
}


